import React, { useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { joinBandWithInvite } from "./bandMembershipService";
import { useTenant } from "./contexts/TenantContext";
import { useToast } from "./contexts/ToastContext";
import { BandWorkspaceProvider, useBandWorkspace } from "./contexts/BandWorkspaceContext";
import { SoloTrigger } from "./features/upgrade/useUpgradeModal";
import Login from "./login";
import JoinLanding from "./JoinLanding";
import LandingPage from "./LandingPage";
import PricingPage from "./PricingPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import BandWorkspace from "./components/BandWorkspace";
import OnboardingFlow from "./components/onboarding/OnboardingFlow";
import ScrollToTop from "./components/ScrollToTop";
import './App.css';

// Bridge: reads workspace context and passes expected props to OnboardingFlow
function OnboardingWrapper() {
  const { band, tenantContext, tier, songs, setlists, showSolo } = useBandWorkspace();
  return (
    <OnboardingFlow
      bandId={band.id}
      tenantContext={tenantContext}
      songs={songs.songs}
      setlists={setlists.setlists}
      isSongsLoading={songs.isLoading}
      isSetlistsLoading={setlists.isSetlistsLoading}
      currentSongCount={songs.songs.length}
      songLimit={tier.songLimit}
      onLimitReached={() => showSolo(SoloTrigger.SONG_LIBRARY_LIMIT_REACHED)}
      onSaved={() => {}}
    />
  );
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToast();
  const {
    user,
    tenantContext,
    bandId: activeBandId,
    isReady,
    beginLogin,
    finishLogin,
  } = useTenant();

  const [inviteFlow, setInviteFlow] = useState({
    inviteMode: false,
    inviteCode: "",
    authCompletePendingRedemption: false,
  });
  const [joinSuccessMessage, setJoinSuccessMessage] = useState("");
  const [showJoinSuccess, setShowJoinSuccess] = useState(false);
  const joinSuccessTimer = useRef(null);

  const JOIN_SUCCESS_MS = 1200;
  const JOIN_ERROR = {
    INVALID: "INVALID_CODE",
    EXPIRED: "EXPIRED_CODE",
    CONSUMED: "INVITE_CONSUMED",
    NETWORK: "NETWORK_ERROR",
  };

  useEffect(() => () => clearTimeout(joinSuccessTimer.current), []);

  useEffect(() => {
    const handler = () => showToast("App updated — refresh to get the latest version.");
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, [showToast]);

  const normalizeJoinError = (err) => {
    const message = err?.message || "";
    if (/expired/i.test(message)) return JOIN_ERROR.EXPIRED;
    if (/invite not found/i.test(message)) return JOIN_ERROR.INVALID;
    if (/already been used|no longer exists/i.test(message)) return JOIN_ERROR.CONSUMED;
    return JOIN_ERROR.NETWORK;
  };

  const showJoinSuccessThenRedirect = (message) => {
    clearTimeout(joinSuccessTimer.current);
    setJoinSuccessMessage(message);
    setShowJoinSuccess(true);
    joinSuccessTimer.current = setTimeout(() => {
      setShowJoinSuccess(false);
      setJoinSuccessMessage("");
      navigate("/");
    }, JOIN_SUCCESS_MS);
  };

  // Post-auth invite redemption — fires only on login/signup, never on page refresh
  const handleLoginComplete = async (firebaseUser, firestoreData, authContext = {}) => {
    beginLogin(firebaseUser);

    const pendingCode = sessionStorage.getItem("pendingInviteCode");
    const storedBandName = sessionStorage.getItem("pendingInviteBandName") || "";

    if (pendingCode) {
      setInviteFlow({ inviteMode: true, inviteCode: pendingCode, authCompletePendingRedemption: true });
      sessionStorage.removeItem("pendingInviteCode");
      sessionStorage.removeItem("pendingInviteBandName");
      sessionStorage.removeItem("pendingInviteStatus");

      let result = null;
      let joinErrorCode = null;

      try {
        result = await joinBandWithInvite({ uid: firebaseUser.uid, token: pendingCode });
      } catch (err) {
        joinErrorCode = normalizeJoinError(err);
      }

      const seed = result ? { lastActiveBandId: result.bandId } : (firestoreData || {});
      try {
        await finishLogin(firebaseUser, seed);
      } catch {}

      setInviteFlow({ inviteMode: true, inviteCode: pendingCode, authCompletePendingRedemption: false });

      if (!joinErrorCode) {
        const joinedBandName = result?.bandName || storedBandName || "your band";
        showToast(`You've joined ${joinedBandName}! Welcome to the band. 🎸`);
        if (authContext?.inviteMode && authContext?.authEntry === "signup") {
          showJoinSuccessThenRedirect(`You've joined "${joinedBandName}"`);
        } else {
          navigate("/");
        }
      } else if (joinErrorCode === JOIN_ERROR.EXPIRED) {
        showToast("This invite link has expired. Ask your band admin for a new one.");
        navigate("/join?error=expired");
      } else if (joinErrorCode === JOIN_ERROR.INVALID) {
        showToast("This invite link is invalid. Ask your band admin for a new one.");
        navigate("/join?error=invalid");
      } else if (joinErrorCode === JOIN_ERROR.CONSUMED) {
        showToast(`You're already a member of ${storedBandName || "this band"}, or the invite has already been used.`);
        navigate("/join?error=invalid");
      } else {
        showToast("Something went wrong joining the band. Please try again.");
        navigate("/join?error=network");
      }
      return;
    }

    try {
      await finishLogin(firebaseUser, firestoreData || {});
      setInviteFlow({ inviteMode: false, inviteCode: "", authCompletePendingRedemption: false });
    } catch (err) {
      console.error("Error finalizing login:", err);
    }
  };

  const isAuthed = !!user;
  const hasTenantBand = !!activeBandId;

  const showInviteTransition = inviteFlow.authCompletePendingRedemption || showJoinSuccess;
  const inviteTransitionTitle = showJoinSuccess
    ? (joinSuccessMessage || "You're in")
    : "Completing your band invite...";
  const inviteTransitionBody = showJoinSuccess
    ? "Opening your Band's workspace..."
    : "Finalizing your membership and opening your band workspace...";

  if (showInviteTransition) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base px-6">
        <div className="rounded-md border border-border bg-surface px-8 py-7 text-center shadow-xl">
          <h2 className="mb-2 text-xl font-semibold text-text-primary">{inviteTransitionTitle}</h2>
          <p className="text-sm text-text-secondary">{inviteTransitionBody}</p>
        </div>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-base px-6">
        <h2 className="text-sm font-medium text-text-secondary">Loading your band...</h2>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/join" element={<JoinLanding />} />
        <Route
          path="/login"
          element={
            isAuthed
              ? hasTenantBand ? <Navigate to="/" replace /> : null
              : <Login key="login-form" onLogin={handleLoginComplete} initialMode={location.state?.mode || "login"} />
          }
        />
        <Route
          path="/signup"
          element={
            isAuthed
              ? hasTenantBand ? <Navigate to="/" replace /> : null
              : <Login key="signup-form" onLogin={handleLoginComplete} initialMode="signup" />
          }
        />
        <Route
          path="/onboarding"
          element={
            !isAuthed ? <Navigate to="/" replace /> :
            hasTenantBand ? <Navigate to="/" replace /> :
            <Navigate to="/signup" replace />
          }
        />
        <Route
          path="/"
          element={
            !isAuthed ? <LandingPage /> :
            !hasTenantBand ? <Navigate to="/onboarding" replace /> :
            <BandWorkspaceProvider>
              <BandWorkspace />
            </BandWorkspaceProvider>
          }
        />
        <Route
          path="/welcome"
          element={
            !isAuthed ? <Navigate to="/" replace /> :
            !hasTenantBand ? <Navigate to="/onboarding" replace /> :
            <BandWorkspaceProvider>
              <OnboardingWrapper />
            </BandWorkspaceProvider>
          }
        />
        <Route path="/start" element={<Navigate to="/" replace />} />
        <Route path="/pricing" element={<PricingPage tenantContext={tenantContext} />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default App;
